/**
 * module : SLTSnowApp.js
 * author: Rob Blanch
 * usage: main application file for UI component of the Stony Lane Tech main web site
 * license: see https://www.stonylanetech.com/licenses/Snow/license.txt for details
 * 
 * (c) 2024 Stony Lane Tech LLC
 */
import React, { Component } from 'react';
import SLTSnowLogo from './slt_stonelogo_banner/slt_stonelogo_banner.png';
import './SLTSnowApp.css';
import './SelectionTable.css';
import { Switch, Route } from 'react-router-dom';
import SLTFooter from './SLTFooter.js';
import TabBar from './TabBar.js';
import SLTLoginPage from './SLTLoginPage.js';
import RegisterForm from './RegisterForm.js';
import ValidationForm from './ValidationForm.js';
import ResetForm from './ResetForm.js';
import RemoveForm from './RemoveForm.js';
import LogoutForm from './LogoutForm.js';
import Version from './AppVersion.js';
import AppPost from './AppPost.js';
import UsageStatistics from './UsageStatistics.js';
import sltDateTime from './sltDateTime.js';
import SLTSnow_api from './SLTSnow_api.js';
import SLTSnowAccountTab from './SLTSnowAccountTab.js';
import SLTHomePage from './SLTHomePage.js';
import SLTLoadPOAData from './SLTLoadPOAData.js';
import SLTNeighborDirectory from './SLTNeighborDirectory.js';

const SnowCopyright = ' 2025 Reserve at Summit Rock POA';

/**
 * SLT Snow App class which drives page layout for Stony Lane Tech main application
 */
class SLTSnowApp extends Component {
  /**
   * constructor for SLTSnowApp
   */
  constructor(props) {
    super(props);

    this.sltDateTime = new sltDateTime();

    var sltsnowapi = new SLTSnow_api();

    this.state = {
      message: '',
      logo: SLTSnowLogo,
      sltsnowapi: sltsnowapi,
      isMounted: (sltsnowapi) ? sltsnowapi.doesSessionExist() : false,
      isNavBarExpanded: false,
      isLoggedIn: false,
      tableDisplayRowIndexes: {}
    }

    /**
     * used to reference actions to be supplied
     */
    this.actionHandler = this.actionHandler();

    /**
     * setup session event listeners
     */
    this.state.sltsnowapi.registerListener('session-logon', this.loginSessionEvent.bind(this));
    this.state.sltsnowapi.registerListener('session-refresh', this.refreshSessionEvent.bind(this));
    this.state.sltsnowapi.registerListener('session-logout', this.logoutSessionEvent.bind(this));
  }

  /**
   * setMessage
   *  set the message for user to see
   */
  setMessage(message) {
    this.setState({message: message});
  }

  /**
   * loginSessionEvent
   *  called by sltsnowapi when user logs into a new session
   */
  loginSessionEvent(session) {
    console.log('SLTSnowApp::loginSessionEvent [INFO]: session login detected.');
  }

   /**
   * refreshSessionEvent
   *  called by sltsnowapi when refresh of session occurs with service
   */
  refreshSessionEvent(newSession, oldSession) {
    /**
     * nothing to do for now, but log to console for info purposes
     */
    console.log('SLTSnowApp::refreshSessionEvent[INFO]: session refresh occurred');
  }

  /**
   * logoutSessionEvent
   *  called by sltsnowapi when user logs out of session
   */
  logoutSessionEvent(session) {
    console.log('SLTSnowApp:logoutSessionEvent [INFO]: session logout detected.');
  }

  /**
   * getUserProperties
   *  retrieve all of the user properties for session user. onComplete(user_properties)
   * will be called on success.
   */
  getUserProperties(onComplete) {
    try {
      this.state.sltsnowapi.getUser(
        (response) => {
          var user_properties = (response.user_properties) ? response.user_properties : null;
          onComplete(user_properties);
        },
        (error) => {
          this.state.message.set(error.message);
          console.log('SLTSnowApp::getUserProperties() [ERROR]: Exception encountered. error.message=' + error.message);
        }
      );
    } catch (err) {
      console.log('SLTSnowApp::getUserProperties() [FATAL ERROR]: Exception thrown during load user properties from service. err=' + JSON.stringify(err));
    }
  }

  /**
   * setUserProperties
   *  set or put user properties to user of session based on param flag. onComplete(response) will be called on success.
   */
  setUserProperties(should_put_properties, user_properties, onComplete) {
    try {
      this.state.sltsnowapi.persistUserProperties(
        (response) => {
          onComplete(response);
        },
        (error) => {
          this.state.message.set(error.message);
          console.log('SLTSnowApp::setUserProperties() [ERROR]: Exception encountered. error.message=' + error.message);
        },
        should_put_properties,
        user_properties
      );
    } catch (err) {
      console.log('SLTSnowApp::setUserProperties() [FATAL ERROR]: Exception thrown during setting user properties to service. err=' + JSON.stringify(err));
    }
  }

  /**
   * actionHandler
   *  called to perform action within the application based on event
   */
  actionHandler() {
    var handler = {};

    /**
     * TODO (ROB 4/15/19): for now i'm flattening all actions at root level for the application,
     *  but really should move things down appropriately for improved design for maintenance of application
     */

    /** global error handler so things can be reported anywhere **/
    handler.errorHandler = this.errorHandler.bind(this);

    /**  get usage statistics for service **/
    handler.getUsageStatistics = this.getUsageStatistics.bind(this);

    /**
     * get/set user properties
     */
    handler.getUserProperties = this.getUserProperties.bind(this);
    handler.setUserProperties = this.setUserProperties.bind(this);
    
    return handler;
  }

  /**
   * getUsageStatistics
   *  handler for obtaining usage statistics of service
   */
  getUsageStatistics(onComplete, onError) {
    this.state.sltsnowapi.getUsageStatistics(
      (response) => {
        onComplete(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /**
   * errorHandler
   *  error handling routine for the application
   */
  errorHandler(sltSnowErrCode, strErrMessage) {
    console.log(" [SLTSnowErrCode: " + sltSnowErrCode + "] - " + strErrMessage);
    this.setMessage(strErrMessage);
  }

    /** 
   * getDisplayRowIndex
   *    routine to obtain the row index for a table of tableName. if tablename
   * not found then index of zero is returned.
   */
  getDisplayRowIndex(tablename) {
    var rowindex = 0;

    if (tablename && this.state.tableDisplayRowIndexes && tablename in this.state.tableDisplayRowIndexes) {
      rowindex = this.state.tableDisplayRowIndexes[tablename].rowIndex;
    }

    return rowindex;
  }

  /**
   * setDisplayRowIndex
   *      routine to stash the row index for a table of tableName
   */
  setDisplayRowIndex(tableName, rowIndex) {
    var tableDisplayIndexes = (this.state.tableDisplayRowIndexes) ? JSON.parse(JSON.stringify(this.state.tableDisplayRowIndexes)) : [];
    var entry = {
      rowIndex: rowIndex,
      stashed: new Date()
    };

    if (tableName in tableDisplayIndexes) {
        tableDisplayIndexes[tableName] = entry;
    } else {
        tableDisplayIndexes[tableName] = entry;
    }

    this.setState({tableDisplayRowIndexes: tableDisplayIndexes});
  }

  /**
   * setNavBarExpanded
   *  callback routine to set a new expanded state for the header nav bar menu
   * 
   * newExpandState is true to expand the nav bar menu and false to not.
   */
  setNavBarExpanded(newExpandState) {
    this.setState({isNavBarExpanded: newExpandState});
  }

  /**
   * onLogin
   *  callback routine when user logs into system
   */
  onLogin(onComplete,
          onError,
          identifier = null,
          password = null) {
    if (password === null || password === undefined || password === '') {
      // attempt passkey authentication if password is not provided
      return this.state.sltsnowapi.passkeyAuthenticate(
        identifier,
        (response) => {
          console.log('SLTSnowApp::onLogin() [INFO]: passkey authentication successful.');
          if (onComplete !== null) {
            this.setState({isLoggedIn: true}, () => onComplete(response));
          }
        },
        (error) => {
          console.log('SLTSnowApp::onLogin() [WARN]: passkey authentication failed.');
          if (onError !== null) {
            onError(error);
          }
        });
    } else {
      // use standard username/password authentication if password is provided
      this.state.sltsnowapi.getSession(
        (response) => {
          console.log('SLTSnowApp::constructor() [INFO]: session load successful.');
          if (onComplete !== null) {
            this.setState({isLoggedIn: true}, () => onComplete(response));
          }
        },
        (error) => {
          console.log('SLTSnowApp::constructor() [WARN]: session load failed. probably not logged in yet.');
          if (onError !== null) {
            onError(error);
          }
        },
        identifier,
        password);
    }
  }

  onDiscoverableLogin(onComplete, onError, options) {
    return this.state.sltsnowapi.discoverablePasskeyAuthenticate(
      result => this.setState({isLoggedIn: true}, () => onComplete(result)), onError, options);
  }

  /**
   * onLogout
   * callback routine when user logs out of system
   */
  onLogout(onComplete, onError) {
    this.state.sltsnowapi.deleteSession(
      (response) => {
        console.log('SLTSnowApp::onLogout() [INFO]: session delete successful.');
        this.setState({isLoggedIn: false});
        if (onComplete !== null) {
          onComplete(response);
        }
      },
      (error) => {
        console.log('SLTSnowApp::onLogout() [WARN]: session delete failed.');
        if (onError !== null) {
          onError(error);
        }
      }
    );
  }

  handleSessionInvalid(onComplete) {
    this.state.sltsnowapi.clearSessionCache();
    this.setState({isLoggedIn: false}, onComplete);
  }

  /**
   * onGetUserPasskeys
   * callback routine when user retrieves their passkeys
   */
  onGetUserPasskeys(onComplete, onError) {
    this.state.sltsnowapi.getUserPasskeys(
      (response) => {
        console.log('SLTSnowApp::onGetUserPasskeys() [INFO]: get user passkeys successful.');
        if (onComplete !== null) {
          onComplete(response);
        }
      },
      (error) => {
        console.log('SLTSnowApp::onGetUserPasskeys() [WARN]: get user passkeys failed.');
        if (onError !== null) {
          onError(error);
        }
      }
    );
  }

  /**
   * onCreatePasskey
   * callback routine when user creates a new passkey for their account
   */
  onCreatePasskey(onComplete, onError) {
    this.state.sltsnowapi.createPasskey(
      (response) => {
        console.log('SLTSnowApp::onCreatePasskey() [INFO]: passkey creation successful.');
        if (onComplete !== null) {
          onComplete(response);
        }
      },
      (error) => {
        console.log('SLTSnowApp::onCreatePasskey() [WARN]: passkey creation failed.');
        if (onError !== null) {
          onError(error);
        }
      }
    );
  }

  /**
   * onDeletePasskey
   * callback routine when user deletes a passkey
   */
  onDeletePasskey(credential_id, onComplete, onError) {
    this.state.sltsnowapi.deletePasskey(
      (response) => {
        console.log('SLTSnowApp::onDeletePasskey() [INFO]: passkey deletion successful.');
        if (onComplete !== null) {
          onComplete(response);
        }
      },
      (error) => {
        console.log('SLTSnowApp::onDeletePasskey() [WARN]: passkey deletion failed.');
        if (onError !== null) {
          onError(error);
        }
      },
      credential_id
    );
  }

  /**
   * renderPageDetails
   *  Renders the page details based on user location in the application
   */
  renderPageDetails() {
    const homeIcon = require('./slt_home_icon/GoldenBearImage.svg');

    const logoItem = {
      link:'/',
      label:'Home',
      icon:homeIcon
    };
    var setNavBarExpanded = this.setNavBarExpanded.bind(this);

    return (
      <div className="SLTSnowPage">
        <Switch>
          <Route exact path='/' render={(props) => <SLTHomePage  props
                                                                  onSignIn = {() => { props.history.push('/login'); }}
                                        />} />
          <Route exact path='/login' render={(props) => <SLTLoginPage   props 
                                                                isLoggedIn={this.state.isLoggedIn}
                                                                logoItem = {logoItem} 
                                                                isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                setNavBarExpanded = {setNavBarExpanded}
                                                                onDiscoverableLogin={this.onDiscoverableLogin.bind(this)}
                                                                onGetSignInMethods={this.state.sltsnowapi.getSignInMethods.bind(this.state.sltsnowapi)}
                                                                onLogin = {this.onLogin.bind(this)} />} />
          <Route path='/register' render={(props) => <RegisterForm  props 
                                                                    logoItem = {logoItem} 
                                                                    isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                    setNavBarExpanded = {setNavBarExpanded}
                                                                    createUser={this.state.sltsnowapi.createUser.bind(this.state.sltsnowapi)}
                                                                    DefaultAlias={SLTSnow_api.DefaultAlias} />}/>
          <Route path='/validate' render={(props) => <ValidationForm    props 
                                                                        logo={this.state.logo}
                                                                        validateAccount={this.state.sltsnowapi.validateAccount.bind(this.state.sltsnowapi)} />}/>
          <Route path='/reset' render={(props) => <ResetForm    props 
                                                                logo={this.state.logo}
                                                                resetPassword={this.state.sltsnowapi.resetPassword.bind(this.state.sltsnowapi)} />} />
          <Route path='/remove' render={(props) => <RemoveForm  props 
                                                                logo={this.state.logo}
                                                                removeAccount={this.state.sltsnowapi.removeAccount.bind(this.state.sltsnowapi)} />} />
          <Route path='/logout' render={(props) => <LogoutForm  props 
                                                                logoItem = {logoItem} 
                                                                isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                setNavBarExpanded = {setNavBarExpanded}
                                                                onLogout={this.onLogout.bind(this)} />} />
          <Route path='/versioninfo' render={(props) => <Version  props
                                                              logoItem = {logoItem} 
                                                              isNavBarExpanded = {this.state.isNavBarExpanded}
                                                              setNavBarExpanded = {setNavBarExpanded}
                                                              getVersion={this.state.sltsnowapi.getVersion.bind(this.state.sltsnowapi)} />} />
          <Route path='/app_post' render={(props) => <AppPost props
                                                              logo={this.state.logo}
                                                              postApplication={this.state.sltsnowapi.postApplication.bind(this.state.sltsnowapi)} />} />
          <Route path='/usagestats' render={(props) => <UsageStatistics props 
                                                                        logo={this.state.logo} 
                                                                        getUsageStatistics={this.state.sltsnowapi.getUsageStatistics.bind(this.state.sltsnowapi)}/>} />
          <Route path='/loadpoadata' render={(props) => <SLTLoadPOAData props 
                                                                        logoItem = {logoItem} 
                                                                        isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                        setNavBarExpanded = {setNavBarExpanded}
                                                                        loadLotData={this.state.sltsnowapi.loadLotData.bind(this.state.sltsnowapi)}
                                                                        loadOwnerData={this.state.sltsnowapi.loadOwnerData.bind(this.state.sltsnowapi)} />} />
          <Route path='/directory' render={(props) => <SLTNeighborDirectory props 
                                                                        isLoggedIn={this.state.isLoggedIn}
                                                                        onSessionInvalid={this.handleSessionInvalid.bind(this)}
                                                                        logoItem = {logoItem} 
                                                                        isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                        setNavBarExpanded = {setNavBarExpanded}
                                                                        getLotOwners={this.state.sltsnowapi.getLotOwners.bind(this.state.sltsnowapi)}
                                                                        postLotOwners={this.state.sltsnowapi.postLotOwners.bind(this.state.sltsnowapi)}
                                                                        getDisplayRowIndex={this.getDisplayRowIndex.bind(this)}
                                                                        setDisplayRowIndex={this.setDisplayRowIndex.bind(this)}
                                                                        groupbylot={true} 
                                                                        getLotsAsGeoJSON={this.state.sltsnowapi.getLotsAsGeoJSON.bind(this.state.sltsnowapi)}
                                                                        onGetUserPasskeys={this.onGetUserPasskeys.bind(this)}
                                                                        onCreatePasskey={this.onCreatePasskey.bind(this)}
                                                                        onDeletePasskey={this.onDeletePasskey.bind(this)} />} />
          <Route path='/account' render={(props) => <SLTSnowAccountTab props 
                                                                        logoItem = {logoItem} 
                                                                        isNavBarExpanded = {this.state.isNavBarExpanded}
                                                                        setNavBarExpanded = {setNavBarExpanded}
                                                                        getUser={this.state.sltsnowapi.getUser.bind(this.state.sltsnowapi)}
                                                                        updateUser={this.state.sltsnowapi.updateUser.bind(this.state.sltsnowapi)} />} />
        </Switch>

        {this.renderFooter()}
      </div>);
  }

  /**
   * onTabBarDoubleTap
   *  callback for handling reseting page to "home" page based on double tap of tab bar selection
   * parameter for label used of tabbar is provided for figuring out which tab bar was double tapped
   */
  onTabBarDoubleTap(label) {
    /** we don't care -- do nothing **/
  }

  /**
   * renderTabBar
   *  Renders tab bar on all SLT Orange pages
   */
  renderTabBar() {
    const directoryIconNotSelected = require('./sltsnow_directory_icon_notselected/sltsnow_directory_icon_notselected.svg');
    const directoryIconSelected = require('./sltsnow_directory_icon_selected/sltsnow_directory_icon_selected.svg');
    const accountIconNotSelected = require('./slt_account_icon_notselected/slt_account_icon_notselected.png');
    const accountIconSelected = require('./slt_account_icon_selected/slt_account_icon_selected.png');
    var onTabBarDoubleTap = this.onTabBarDoubleTap.bind(this);

    var tabs = [
      {"link": '/directory', "label": 'directory', "iconSelected": directoryIconSelected, "iconNotSelected": directoryIconNotSelected, "onDoubleTap": onTabBarDoubleTap},
      {"link": '/account', "label": 'account', "iconSelected": accountIconSelected, "iconNotSelected": accountIconNotSelected, "onDoubleTap": onTabBarDoubleTap}
    ];

    return (
      <TabBar props tabs={tabs}/>
    );
  }

  /**
   * renderFooter
   *  Renders footer on all SLT Snow pages
   */
  renderFooter() {
    return (
      <SLTFooter
        props
        copyright={SnowCopyright}>
      </SLTFooter>
    );
  }

  /**
   * componentDidMount
   *  reactjs call to indicate component has been mounted which is important since things like setState can not be used prior to mounting
   */
  componentDidMount() {
    if (this.state.isLoggedIn === false) {
      this.state.sltsnowapi.getSession(
        (response) => {
          this.setState({isLoggedIn: true, isMounted: true});
          console.log('SLTSnowApp::componentDidMount() [INFO]: user logged in at mount time.');
        },
        (error) => {
          console.log('SLTSnowApp::componentDidMount() [INFO]: user not logged in at mount time.');
        }
      );
    } else {
      this.setState({isMounted: true});
    }
  }

  /**
   * render
   *  main reactjs call for using JSX to represent UI layout in app element life cycle
   */
  render() {
    return (
      <div className="slt_background">
        <div className="slt_background_overlay">
          <div className="SLTSnowApp">
            {this.renderPageDetails()}
          </div>
        </div>
      </div>
    );
  }
} 

export default SLTSnowApp;
